/* eslint-disable max-len */
const ExcelJS = require('exceljs');
import moment from 'moment';

export default class ExportsFormatter {

	static DATE_FIELDS = [
		'created_on',
		'expires_on',
		'updated_on',
		'registration_date',
		'opening_date',
		'closing_date',
		'blocking_date',
		'resolved_on',
		'first_subscription_date',
		'last_subscription_date',
		'request_date',
		'deleted_on',
		'aml_checked_on',
		'decease_date',
		'deathdate',
		'birthdate',
		'birth_date',
		'lei_expiration_date',
		'first_payment_date',
		'date_of_asset_value',
		'document_expiration_date',
		'operation_date',
		'nav_date',
		'pp_redemption_entitlement_date'
	];

	// context must include:
	// $t; $route; $accountsService; $intervenersService; $usersService; $groupsService; $issuesService; $operationsService; $permissionService;
	constructor(context) {
		this.context = context;
	}

	_checkIsObject(value) {
		return typeof value === 'object' && value !== null;
	}

	_translateField(key, value) {
		if (!key || value === null) {
			return '-';
		}
		const fieldsToTranslationsPathMap = {
			intervener_type: 'interveners.intervener_type',
			account_type: 'accounts.account_type',
			investor_class: 'accounts.investor_class',
			legal_entity_type: 'accounts.intervener_type',
			signature_type: 'accounts.signature_type',
			source: 'interveners.channels',
			withholding_group: 'accounts.withholding_group',
			gender: 'interveners.gender',
			marital_status: 'maritalStatus',
			matrimonial_regime: 'matrimonialRegime',
			contact_language: 'contactLanguages',
			blocking: 'issues.blocking',
			key_operation_type: 'operations.operations_types',
			key_sub_operation_type: 'operations.operations_sub_types',
			category: 'issues.categories',
			document_subcategory: 'documents.subcategories',
			amount_shares_indicator: 'operations.amount_shares_indicator',
			mailing_type: 'interveners.mailing_type',
			document_type: 'interveners.document_type',
			status: 'issues.status',
			key_country_of_birth: 'countries',
			f_address_key_country: 'countries',
			key_country: 'countries',
			key_country_nationality1: 'countries',
			key_country_nationality2: 'countries',
			key_nationality_1: 'countries',
			key_nationality_2: 'countries',
			f_address_key_state: 'spanishProvinces',
			state: 'spanishProvinces',
			object_type: 'issues.object_type',
			// COMENTADOS los campos de conciliación: YURI-2386
			// reconciliation_status: 'operations.reconciliation_statuses',
			videoidentification_status: 'interveners.videoidentification_status'
		};
		const translationPath = fieldsToTranslationsPathMap[key];
		if (!translationPath) {
			return value;
		}
		const translated = this.context.$t(`${translationPath}.${value}`);
		return typeof translated === 'string' && translated.includes(translationPath) ? value : translated;
	}

	_translateObjectValues(object) {
		if (!this._checkIsObject(object)) {
			return;
		}

		Object.keys(object).forEach(key => {
			// translate array
			if (Array.isArray(object[key])) {
				object[key].forEach(arrayObject =>
					this._translateObjectValues(arrayObject), this
				);
				// translate object
			} else if (this._checkIsObject(object[key])) {
				this._translateObjectValues(object[key]);

				// translate value
			} else {
				object[key] = this._translateField(key, object[key]);
			}
		}, this);
	}

	applyTranslations(headers, entities) {
		headers.forEach((value, index, array) => {
			if (typeof value !== 'object') {
				array[index] = { header: this.context.$t(`headers.${value}`), key: value };
			}
		}, this);
		for (let i = 0; i < entities.length; i++) {
			this._translateObjectValues(entities[i]);
		}
	}

	formatEntitiesFields(entities, headers) {
		let longestIntervenersCount = 0;

		entities.forEach(entity =>
			Object.keys(entity).forEach(key => {

				if (key === 'distributor' || key === 'subdistributor') {
					entity[key] = typeof entity[key] === 'string' ? entity[key] : (entity[key] ? entity[key].key_name : null);

				} else if (key === 'interveners') {
					if (longestIntervenersCount < entity[key].length) {
						longestIntervenersCount = entity[key].length;
					}
					entity[key].forEach((intervener, index) =>
						entity[`intervener_${index}`] = intervener.document_id
					);
					delete entity[key];
				} else if (ExportsFormatter.DATE_FIELDS.includes(key)) {
					entity[key] = entity[key] ?
						moment(entity[key])
							.tz('Europe/Madrid')
							.format('DD-MMM-YYYY | HH:mm [h]')
						:
						null;
				}
			})
		);
		for (let i = 0; i < longestIntervenersCount; i++) {
			headers.push({ header: `Interviniente`, key: `intervener_${i}` });
		}
	}

    generateFile(format, headers, entities) {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet();
        worksheet.columns = headers;
        worksheet.addRows(entities);
        return format === 'CSV' ? workbook.csv.writeBuffer() : workbook.xlsx.writeBuffer();
    }

    downloadFile(data, section, format) {
        const fileType = format === 'CSV' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const blob = new Blob([data], { type: fileType });
        const link = document.createElement('a');
        const urlCreator = window.URL || window.webkitURL;
        link.href = urlCreator.createObjectURL(blob);
        link.download = `${section}.${format}`;
        link.click();
    }

    getData(payload) {
        if (typeof payload.sort === 'object') {
            payload.sort = '';
        }
        switch (this.context.$route.name) {
            case 'issues':
                return this.context.$issuesService.getIssues(payload.sort, payload.cfg);
            case 'users':
                return this.context.$usersService.getUsers(payload.sort, payload.cfg);
            case 'groups':
                return this.context.$groupsService.getGroups(payload.sort, payload.cfg);
            case 'permission_set':
                return this.context.$permissionService.getPermissions(payload.sort, payload.cfg);
            case 'interveners':
                payload.cfg.params['$expand'] = 'distributor,subdistributor,videoidentification_status';
				return this.context.$intervenersService.getInterveners(payload.sort, payload.cfg);
            case 'accounts':
			case 'intervener_participant_accounts_uuid_view':
                payload.cfg.params['$expand'] = 'interveners,distributor,subdistributor';
                return this.context.$accountsService.getAccounts(payload.sort, payload.cfg);
            case 'operations':
			case 'related_operations_view':
                payload.cfg.params['$expand'] = 'interveners';
                return this.context.$operationsService.getOperations(payload.sort, payload.cfg);
            default:
                break;
        }
    }

	parseDataResponse(res, section) {

		const sectionMap = {
			permission_set: 'permission_sets',
			related_operations_view: 'operations',
			intervener_participant_accounts_uuid_view: 'accounts'
		};

		const dataKey = sectionMap[section] || section;

		return res.data[dataKey];
	}
}
